import { Link } from 'react-router-dom';
import { 
  ArrowRight, Zap, MapPin, Eye, Monitor, Shield, Globe,
  BarChart3, Clock, AlertTriangle, Check, X, Building2,
  Users, Smartphone, Wrench, Brain, TrendingUp, FileText
} from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

const OperationalImpactPage = () => {
  const { t, language } = useLanguage();
  const getLocalizedPath = (path) => `/${language}${path}`;

  return (
    <div className="min-h-screen" data-testid="operational-impact-page">

      {/* ========== 1. HERO ========== */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-20">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-0 w-[500px] h-[500px] bg-gradient-to-br from-sky-100 to-blue-50 rounded-full blur-3xl" />
        </div>
        <div className="container-max px-6 md:px-12 lg:px-24 relative">
          <div className="text-center max-w-4xl mx-auto">
            <span className="text-caption text-[#0077FF] mb-4 block" data-testid="impact-label">
              {t('operationalImpact.hero.label')}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-[56px] font-bold text-slate-900 leading-tight tracking-tight mb-6" data-testid="impact-title">
              {t('operationalImpact.hero.title')}
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-3xl mx-auto">
              {t('operationalImpact.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* ========== 2. INCIDENT DETECTION ACCELERATION ========== */}
      <section className="section-padding bg-white" data-testid="incident-detection-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-caption text-[#0077FF] mb-4 block">{t('operationalImpact.incidentDetection.label')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              {t('operationalImpact.incidentDetection.title')}
            </h2>
            <div className="space-y-4 text-lg text-slate-600 leading-relaxed text-left">
              <p>{t('operationalImpact.incidentDetection.intro1')}</p>
              <p>{t('operationalImpact.incidentDetection.intro2')}</p>
            </div>
          </div>

          {/* 3 capability cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-16 max-w-5xl mx-auto">
            {(() => {
              const icons = [MapPin, Zap, Smartphone];
              const colors = [
                { bg: 'bg-[#0077FF]/5', iconBg: 'bg-[#0077FF]/10', text: 'text-[#0077FF]', border: 'border-[#0077FF]/20' },
                { bg: 'bg-amber-50', iconBg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200' },
                { bg: 'bg-emerald-50', iconBg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200' }
              ];
              return (t('operationalImpact.incidentDetection.cards') || []).map((card, index) => {
                const Ic = icons[index]; const c = colors[index];
                return (
                  <div key={index} className={`${c.bg} rounded-2xl p-8 border ${c.border}`} data-testid={`detection-card-${index}`}>
                    <div className={`w-14 h-14 ${c.iconBg} rounded-xl flex items-center justify-center mb-6`}>
                      <Ic className={`w-7 h-7 ${c.text}`} />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{card.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{card.description}</p>
                  </div>
                );
              });
            })()}
          </div>

          {/* Before / After comparison */}
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Without */}
              <div className="bg-red-50 rounded-2xl p-8 border border-red-200" data-testid="comparison-before">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <X className="w-5 h-5 text-red-500" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">
                    {t('operationalImpact.incidentDetection.comparison.before.title')}
                  </h3>
                </div>
                <ul className="space-y-3">
                  {(t('operationalImpact.incidentDetection.comparison.before.items') || []).map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-red-400 rounded-full mt-2.5 flex-shrink-0" />
                      <span className="text-slate-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {/* With */}
              <div className="bg-[#0077FF]/5 rounded-2xl p-8 border border-[#0077FF]/20" data-testid="comparison-after">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-[#0077FF]/10 rounded-lg flex items-center justify-center">
                    <Check className="w-5 h-5 text-[#0077FF]" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">
                    {t('operationalImpact.incidentDetection.comparison.after.title')}
                  </h3>
                </div>
                <ul className="space-y-3">
                  {(t('operationalImpact.incidentDetection.comparison.after.items') || []).map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 bg-[#0077FF]/10 rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3.5 h-3.5 text-[#0077FF]" />
                      </div>
                      <span className="text-slate-700 font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== 3. OPERATIONAL VISIBILITY ========== */}
      <section className="section-padding bg-slate-50" data-testid="op-visibility-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <span className="text-caption text-[#0077FF] mb-4 block">{t('operationalImpact.operationalVisibility.label')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              {t('operationalImpact.operationalVisibility.title')}
            </h2>
            <div className="space-y-4 text-lg text-slate-600 leading-relaxed text-left">
              <p>{t('operationalImpact.operationalVisibility.intro1')}</p>
              <p>{t('operationalImpact.operationalVisibility.intro2')}</p>
            </div>
          </div>

          {/* Heatmap illustration */}
          <div className="max-w-[1100px] mx-auto mb-6">
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-slate-200">
              <img loading="eager" decoding="sync" src="/deployment_heatmap.png" alt="Operational Visibility Heatmap" className="w-full h-auto" data-testid="visibility-heatmap-image" />
            </div>
          </div>
          <p className="text-sm text-slate-500 text-center max-w-2xl mx-auto mb-16">
            {t('operationalImpact.operationalVisibility.caption')}
          </p>

          {/* 3 visibility cards */}
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {(() => {
              const icons = [MapPin, Eye, BarChart3];
              const colors = [
                { bg: 'bg-emerald-50', iconBg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200' },
                { bg: 'bg-violet-50', iconBg: 'bg-violet-100', text: 'text-violet-600', border: 'border-violet-200' },
                { bg: 'bg-rose-50', iconBg: 'bg-rose-100', text: 'text-rose-600', border: 'border-rose-200' }
              ];
              return (t('operationalImpact.operationalVisibility.cards') || []).map((card, index) => {
                const Ic = icons[index]; const c = colors[index];
                return (
                  <div key={index} className={`${c.bg} rounded-2xl p-8 border ${c.border}`} data-testid={`visibility-card-${index}`}>
                    <div className={`w-14 h-14 ${c.iconBg} rounded-xl flex items-center justify-center mb-6`}>
                      <Ic className={`w-7 h-7 ${c.text}`} />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{card.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{card.description}</p>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </section>

      {/* ========== 4. RESPONSE COORDINATION ========== */}
      <section className="section-padding bg-white" data-testid="response-coordination-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <span className="text-caption text-[#0077FF] mb-4 block">{t('operationalImpact.responseCoordination.label')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              {t('operationalImpact.responseCoordination.title')}
            </h2>
            <div className="space-y-4 text-lg text-slate-600 leading-relaxed text-left">
              <p>{t('operationalImpact.responseCoordination.intro1')}</p>
              <p>{t('operationalImpact.responseCoordination.intro2')}</p>
            </div>
          </div>

          {/* Command Center screenshot */}
          <div className="max-w-[1000px] mx-auto mb-16">
            <div className="bg-slate-900 rounded-2xl p-1.5 shadow-2xl">
              <div className="bg-slate-800 rounded-t-xl px-4 py-2.5 flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                </div>
                <div className="flex-1 mx-3">
                  <div className="bg-slate-700 rounded-md px-3 py-1 text-[#4DB8FF] text-xs flex items-center gap-2">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <span className="truncate">portal.tapelligence.com/command</span>
                  </div>
                </div>
              </div>
              <div className="rounded-b-xl overflow-hidden">
                <img loading="eager" decoding="sync" src="/screenshot_command_center.png" alt="Tapelligence Command Center" className="w-full h-auto" data-testid="response-command-center-image" />
              </div>
            </div>
          </div>

          {/* Capabilities */}
          <div className="max-w-4xl mx-auto mb-12">
            <div className="grid sm:grid-cols-2 gap-4">
              {(() => {
                const capIcons = [Monitor, MapPin, Clock, AlertTriangle, Building2, BarChart3];
                return (t('operationalImpact.responseCoordination.capabilities') || []).map((cap, index) => {
                  const Ic = capIcons[index] || Check;
                  return (
                    <div key={index} className="flex items-center gap-4 bg-slate-50 rounded-xl p-4 border border-slate-200" data-testid={`capability-${index}`}>
                      <div className="w-10 h-10 bg-[#0077FF]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Ic className="w-5 h-5 text-[#0077FF]" />
                      </div>
                      <span className="text-slate-700 font-medium">{cap}</span>
                    </div>
                  );
                });
              })()}
            </div>
          </div>

          {/* Result callout */}
          <div className="max-w-3xl mx-auto">
            <div className="bg-[#0077FF]/5 rounded-2xl border border-[#0077FF]/20 p-8" data-testid="response-result">
              <h3 className="text-xl font-bold text-slate-900 mb-4">
                {t('operationalImpact.responseCoordination.result.title')}
              </h3>
              <p className="text-slate-700 leading-relaxed text-lg">
                {t('operationalImpact.responseCoordination.result.text')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== 5. ENTERPRISE OPERATIONAL INTELLIGENCE ========== */}
      <section className="section-padding bg-slate-50" data-testid="enterprise-intelligence-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <span className="text-caption text-[#0077FF] mb-4 block">{t('operationalImpact.enterpriseIntelligence.label')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              {t('operationalImpact.enterpriseIntelligence.title')}
            </h2>
            <div className="space-y-4 text-lg text-slate-600 leading-relaxed text-left">
              <p>{t('operationalImpact.enterpriseIntelligence.intro1')}</p>
              <p>{t('operationalImpact.enterpriseIntelligence.intro2')}</p>
            </div>
          </div>

          {/* Analytics screenshot */}
          <div className="max-w-[1000px] mx-auto mb-16">
            <div className="bg-slate-900 rounded-2xl p-1.5 shadow-2xl">
              <div className="bg-slate-800 rounded-t-xl px-4 py-2.5 flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                </div>
                <div className="flex-1 mx-3">
                  <div className="bg-slate-700 rounded-md px-3 py-1 text-[#4DB8FF] text-xs flex items-center gap-2">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <span className="truncate">portal.tapelligence.com/analytics</span>
                  </div>
                </div>
              </div>
              <div className="rounded-b-xl overflow-hidden">
                <img loading="eager" decoding="sync" src="/screenshot_analytics.png" alt="Tapelligence Analytics Dashboard" className="w-full h-auto" data-testid="enterprise-analytics-image" />
              </div>
            </div>
          </div>

          {/* 4 insight cards */}
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {(() => {
              const icons = [Globe, TrendingUp, Brain, FileText];
              const colors = [
                { bg: 'bg-[#0077FF]/5', iconBg: 'bg-[#0077FF]/10', text: 'text-[#0077FF]', border: 'border-[#0077FF]/20' },
                { bg: 'bg-amber-50', iconBg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200' },
                { bg: 'bg-violet-50', iconBg: 'bg-violet-100', text: 'text-violet-600', border: 'border-violet-200' },
                { bg: 'bg-emerald-50', iconBg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200' }
              ];
              return (t('operationalImpact.enterpriseIntelligence.insights') || []).map((insight, index) => {
                const Ic = icons[index]; const c = colors[index];
                return (
                  <div key={index} className={`${c.bg} rounded-2xl p-8 border ${c.border}`} data-testid={`intelligence-card-${index}`}>
                    <div className={`w-14 h-14 ${c.iconBg} rounded-xl flex items-center justify-center mb-6`}>
                      <Ic className={`w-7 h-7 ${c.text}`} />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{insight.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{insight.description}</p>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </section>

      {/* ========== 6. CLOSING CTA ========== */}
      <section className="py-20 bg-slate-900" data-testid="impact-closing-section">
        <div className="container-max px-6 md:px-12 lg:px-24 text-center">
          <p className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight max-w-4xl mx-auto mb-10">
            {t('operationalImpact.closing')}
          </p>
          <Link to={getLocalizedPath('/contact')} className="btn-primary bg-white text-slate-900 hover:bg-slate-100 text-base" data-testid="impact-cta">
            {t('home.hero.cta')}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default OperationalImpactPage;
